import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { features } from './data';
import type { FeatureData } from './FeaturePage';
import SEO, { breadcrumbJsonLd } from '../../components/SEO';

/* Same order as Features mega menu: Tracking & Delivery first, then Operations & Insights */
const FEATURE_SLUGS = [
  'branded-tracking',
  'route-planner',
  'proof-of-delivery',
  'driver-app',
  'ai-agents',
  'auto-allocation',
  'cash-management',
  'reports-analytics',
];

export default function FeaturesIndex() {
  const { t } = useTranslation();
  const list = FEATURE_SLUGS.map((slug) => features[slug]).filter(Boolean);

  return (
    <div className="pt-24">
      <SEO
        title={t('features.index.seo.title')}
        description={t('features.index.seo.description')}
        jsonLd={breadcrumbJsonLd([
          { name: t('common.breadcrumb.home'), url: 'https://geofleet.ai' },
          { name: t('common.breadcrumb.features'), url: 'https://geofleet.ai/features' },
        ])}
      />
      {/* Hero with stats strip */}
      <section className="relative py-16 lg:py-20 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-blue-500/[0.06] rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[250px] bg-emerald-500/[0.05] rounded-full blur-3xl -translate-x-1/2" />
        <div className="absolute top-1/2 right-1/4 w-[200px] h-[200px] bg-violet-500/[0.04] rounded-full blur-3xl" />
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-apps-line" />
            <span>{t('features.index.badge')}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-gray-900 tracking-tight mb-4">
            {t('features.index.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            {t('features.index.subtitle')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-gray-900">8+</div>
              <div className="text-sm text-gray-500 mt-0.5">{t('features.index.stats.features')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-blue-600">{t('features.index.stats.aiPowered')}</div>
              <div className="text-sm text-gray-500 mt-0.5">{t('features.index.stats.dispatchRouting')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-emerald-600">99.99%</div>
              <div className="text-sm text-gray-500 mt-0.5">{t('features.index.stats.uptime')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature cards — two groups to match mega menu */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Tracking & Delivery */}
          <div className="mb-14">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <i className="ri-map-pin-user-line text-blue-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{t('nav.panels.features.trackingDelivery')}</h2>
            </div>
            <p className="text-sm text-gray-500 mb-6 max-w-2xl">{t('nav.panels.features.trackingDeliverySub')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {list.slice(0, 4).map((feature) => (
                <FeatureCard key={feature.slug} feature={feature} />
              ))}
            </div>
          </div>

          {/* Operations & Insights — emerald accent */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <i className="ri-dashboard-3-line text-emerald-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{t('nav.panels.features.operationsInsights')}</h2>
            </div>
            <p className="text-sm text-gray-500 mb-6 max-w-2xl">{t('nav.panels.features.operationsInsightsSub')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {list.slice(4, 8).map((feature) => (
                <FeatureCard key={feature.slug} feature={feature} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('features.index.cta.title')}</h2>
          <p className="text-gray-600 mb-6">{t('features.index.cta.subtitle')}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
            >
              {t('common.ctaStartTrial')}
              <i className="ri-arrow-right-line text-sm" />
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-emerald-700 border-2 border-emerald-500 hover:bg-emerald-50 transition-all"
            >
              <i className="ri-calendar-line text-emerald-600" />
              {t('common.ctaBookDemo')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({ feature }: { feature: FeatureData }) {
  const { t } = useTranslation();
  const bg = feature.accentColorLight ?? '#eef5ff';
  const color = feature.accentColor ?? '#2563eb';
  return (
    <Link
      to={`/features/${feature.slug}`}
      className="group block bg-white rounded-2xl border border-gray-100 p-6 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-300"
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300"
        style={{ backgroundColor: bg }}
      >
        <i className={`${feature.icon} text-xl`} style={{ color }} />
      </div>
      <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
        {feature.name}
      </h3>
      <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4">
        {feature.description}
      </p>
      <span
        className="inline-flex items-center gap-1.5 text-sm font-semibold"
        style={{ color }}
      >
        {t('features.index.learnMore')}
        <i className="ri-arrow-right-line text-base group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}
